/**
 * The tenant host half of the tenant hand-off (customer onboarding S5, WS-15, ruling R-062 as
 * R-064 item 1 amends it; the core-api route `POST /v1/auth/handoff/token` of S4 R-050 item 4).
 *
 * On the tenant host, the login page of `@zion/shell-system-surfaces` keeps a record in this tab's
 * `sessionStorage` (`requestTenantHandoff`): the PKCE verifier, the state and the landing. The DP
 * host signs the person in, asks core-api for a one-time code bound to the record's challenge, and
 * sends the browser to `https://<tenant host>/auth/handoff-callback#code=<code>&state=<state>`
 * (`beginTenantHandoff`). On the tenant host the boot sees that path
 * ({@link isTenantHandoffCallbackPath}) and calls {@link redeemTenantHandoff} with `location.hash`.
 * The two halves share the names of `@zion/shell-system-surfaces/tenant-handoff-wire`.
 *
 * The verifier never leaves the tab that started the hand-off, so a callback link from another
 * browser finds no record and redeems nothing (WS-15-P2-R1, R-064 item 1). The module takes the
 * shape of its sibling `kernel/kernel-handoff.ts` (defaults table F5): one error class with a
 * failure union, a one-use record with a lifetime, a length cap, an exact name set, the contract
 * schemas, and a bounded token request. The kernel hand-off keeps its bytes (R-062 item 4). No log
 * line or error message here holds a code, a verifier or a state.
 */
import {
  KERNEL_HANDOFF_CODE_PATTERN,
  KERNEL_HANDOFF_STATE_PATTERN,
  KERNEL_RETURN_PATH_MAX_LENGTH,
  KERNEL_RETURN_PATH_PATTERN,
  PKCE_CODE_VERIFIER_PATTERN,
  SESSION_TRANSPORT_COOKIE,
  SESSION_TRANSPORT_HEADER,
  sessionResponseSchema,
  type SessionResponse,
} from '@zion/sdk-contracts';
import { resolvePostLoginPath } from '@zion/shell-system-surfaces/protocol';
import {
  MAX_TENANT_CALLBACK_FRAGMENT_LENGTH,
  TENANT_HANDOFF_CALLBACK_PATH,
  TENANT_HANDOFF_FRAGMENT_PARAMS,
  TENANT_HANDOFF_STORAGE_KEY,
  TENANT_HANDOFF_TTL_MS,
  type TenantHandoffRecord,
} from '@zion/shell-system-surfaces/tenant-handoff-wire';

/**
 * The bound of the token request, in milliseconds: 10,000. A request that runs longer aborts and
 * reads `unavailable`, so a hung server never holds the boot's routing chain.
 */
export const TENANT_TOKEN_TIMEOUT_MS = 10_000 as const;

/** The statuses that mean core-api refused this code: a retry with the same code cannot pass. */
const REFUSED_STATUSES: ReadonlySet<number> = new Set([400, 401, 403, 404, 409, 410]);

/**
 * Why a redeem stopped.
 *
 * - `malformed-callback`: the fragment fails the length cap, the name set or the schema.
 * - `no-record`: this tab holds no hand-off record, or the record does not parse.
 * - `state-mismatch`: the fragment's state differs from the record's.
 * - `expired`: the record is older than `TENANT_HANDOFF_TTL_MS`.
 * - `token-refused`: core-api refused the code (400, 401, 403, 404, 409 or 410).
 * - `unavailable`: the token request failed, timed out or answered another non-2xx status.
 * - `malformed-session`: a 2xx answer that is not a session.
 */
export type TenantCallbackFailure =
  | 'malformed-callback'
  | 'no-record'
  | 'state-mismatch'
  | 'expired'
  | 'token-refused'
  | 'unavailable'
  | 'malformed-session';

/**
 * The one error the redeem rejects with. Its message names the failure and nothing else.
 *
 * `returnTo` is the landing of the record this tab kept, once the redeem read a valid record: a
 * same-origin path that passed `KERNEL_RETURN_PATH_PATTERN`. It is `null` for a failure before the
 * record read (`malformed-callback` of the length cap, `no-record`). The boot carries it to the
 * login page, so a sign-in after a failed redeem still lands where the person asked.
 */
export class TenantCallbackError extends Error {
  override readonly name = 'TenantCallbackError';
  readonly failure: TenantCallbackFailure;
  readonly returnTo: string | null;

  constructor(failure: TenantCallbackFailure, returnTo: string | null = null) {
    super(`the tenant hand-off stopped: ${failure}`);
    this.failure = failure;
    this.returnTo = returnTo;
  }
}

/** A redeemed code: the session core-api answered, and the path the tab lands on. */
export interface RedeemedTenantSession {
  readonly session: SessionResponse;
  readonly landing: string;
}

/** The three members of Web Storage the redeem uses. The boot passes the tab's `sessionStorage`. */
export type TenantCallbackStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>;

/**
 * What {@link redeemTenantHandoff} runs against.
 *
 * - `fetch`: the same-origin fetch of the boot.
 * - `storage`: the tab's `sessionStorage`, which holds the record of the hand-off this tab started.
 * - `now`: the clock in epoch milliseconds.
 */
export interface TenantCallbackDeps {
  readonly fetch: typeof fetch;
  readonly storage: TenantCallbackStorage;
  readonly now: () => number;
}

/**
 * True when `pathname` is `TENANT_HANDOFF_CALLBACK_PATH` (`/auth/handoff-callback`), case folded,
 * with one trailing slash removed, as the router compares paths. The path sits under the reserved
 * `auth` segment, so no app can mount there. Pure.
 */
export function isTenantHandoffCallbackPath(pathname: string): boolean {
  const trimmed = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
  return trimmed.toLowerCase() === TENANT_HANDOFF_CALLBACK_PATH;
}

/** True when `value` is a record `requestTenantHandoff` wrote: every member present, with its grammar. */
function isTenantRecord(value: unknown): value is TenantHandoffRecord {
  if (typeof value !== 'object' || value === null) return false;
  const record = value as Readonly<Record<string, unknown>>;
  return (
    record.v === 1 &&
    typeof record.state === 'string' &&
    KERNEL_HANDOFF_STATE_PATTERN.test(record.state) &&
    typeof record.verifier === 'string' &&
    PKCE_CODE_VERIFIER_PATTERN.test(record.verifier) &&
    typeof record.returnTo === 'string' &&
    record.returnTo.length <= KERNEL_RETURN_PATH_MAX_LENGTH &&
    KERNEL_RETURN_PATH_PATTERN.test(record.returnTo) &&
    typeof record.createdAt === 'number' &&
    Number.isFinite(record.createdAt)
  );
}

/** Read the record and remove it in the same step, so one record serves one callback at most. */
function takeRecord(storage: TenantCallbackStorage): TenantHandoffRecord {
  let parsed: unknown;
  try {
    const raw = storage.getItem(TENANT_HANDOFF_STORAGE_KEY);
    storage.removeItem(TENANT_HANDOFF_STORAGE_KEY);
    parsed = raw === null ? null : JSON.parse(raw);
  } catch {
    throw new TenantCallbackError('no-record');
  }
  if (!isTenantRecord(parsed)) throw new TenantCallbackError('no-record');
  return parsed;
}

/**
 * The code and the state of a fragment that holds each name once and no other. A refusal carries
 * `returnTo`, the landing of the record already read.
 */
function fragmentOf(hash: string, returnTo: string): { readonly code: string; readonly state: string } {
  const params = new URLSearchParams(hash.startsWith('#') ? hash.slice(1) : hash);
  const names = [...params.keys()];
  const exact =
    names.length === TENANT_HANDOFF_FRAGMENT_PARAMS.length &&
    TENANT_HANDOFF_FRAGMENT_PARAMS.every((name) => params.getAll(name).length === 1);
  if (!exact) throw new TenantCallbackError('malformed-callback', returnTo);
  const [codeName, stateName] = TENANT_HANDOFF_FRAGMENT_PARAMS;
  // The exact name check above guarantees one value of each name.
  const code = String(params.get(codeName));
  const state = String(params.get(stateName));
  if (!KERNEL_HANDOFF_CODE_PATTERN.test(code) || !KERNEL_HANDOFF_STATE_PATTERN.test(state)) {
    throw new TenantCallbackError('malformed-callback', returnTo);
  }
  return { code, state };
}

/**
 * Redeem the code of a tenant callback and answer the session and the landing.
 *
 * The boot calls it once, on the callback path, with `location.hash`. It checks in this order;
 * each failure rejects with a {@link TenantCallbackError}, and every failure before step 6 sends
 * nothing:
 *
 * 1. A fragment over `MAX_TENANT_CALLBACK_FRAGMENT_LENGTH` (2,560) characters rejects
 *    `malformed-callback`.
 * 2. It reads the record under `TENANT_HANDOFF_STORAGE_KEY` and removes it at once, so one record
 *    serves one callback at most. No record, a storage that throws, bad JSON, a `v` other than `1`,
 *    a state, a verifier or a `returnTo` outside its pattern, or a `createdAt` that is not a finite
 *    number rejects `no-record`.
 * 3. A fragment whose names are not exactly `code` and `state` once each, a code outside
 *    `KERNEL_HANDOFF_CODE_PATTERN`, or a state outside `KERNEL_HANDOFF_STATE_PATTERN` rejects
 *    `malformed-callback`.
 * 4. A state other than the record's rejects `state-mismatch`.
 * 5. A record older than `TENANT_HANDOFF_TTL_MS` (600,000 ms) rejects `expired`.
 * 6. It POSTs `/v1/auth/handoff/token` same-origin with `SESSION_TRANSPORT_HEADER: cookie` and the
 *    body `{ code, codeVerifier }`, the verifier from the record, bounded by
 *    {@link TENANT_TOKEN_TIMEOUT_MS}, which bounds the body read too. A rejected or timed-out
 *    fetch rejects `unavailable`, and so does a body read that the bound stopped. A 400, 401, 403,
 *    404, 409 or 410 rejects `token-refused`; any other non-2xx rejects `unavailable`. A 2xx body
 *    that is not JSON or that `sessionResponseSchema` refuses rejects `malformed-session`.
 * 7. It answers `{ session, landing: resolvePostLoginPath(record.returnTo) }`. The fragment never
 *    sets the landing. core-api sets the host-only refresh and access cookies in its reply.
 *
 * Every failure from step 3 on carries the record's `returnTo` (see {@link TenantCallbackError}).
 * It never adopts the session; the boot does.
 */
export async function redeemTenantHandoff(deps: TenantCallbackDeps, hash: string): Promise<RedeemedTenantSession> {
  if (hash.length > MAX_TENANT_CALLBACK_FRAGMENT_LENGTH) throw new TenantCallbackError('malformed-callback');
  const record = takeRecord(deps.storage);
  const fail = (failure: TenantCallbackFailure): TenantCallbackError => new TenantCallbackError(failure, record.returnTo);
  const fragment = fragmentOf(hash, record.returnTo);
  if (fragment.state !== record.state) throw fail('state-mismatch');
  if (deps.now() - record.createdAt > TENANT_HANDOFF_TTL_MS) throw fail('expired');
  // One signal bounds the headers and the body read: an abort errors the body stream of the response.
  const bound = AbortSignal.timeout(TENANT_TOKEN_TIMEOUT_MS);
  let response: Response;
  try {
    response = await deps.fetch('/v1/auth/handoff/token', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json', [SESSION_TRANSPORT_HEADER]: SESSION_TRANSPORT_COOKIE },
      body: JSON.stringify({ code: fragment.code, codeVerifier: record.verifier }),
      signal: bound,
    });
  } catch {
    throw fail('unavailable');
  }
  if (!response.ok) throw fail(REFUSED_STATUSES.has(response.status) ? 'token-refused' : 'unavailable');
  let json: unknown;
  try {
    json = await response.json();
  } catch {
    // A body read the bound stopped is a server that did not answer in time, not a bad session.
    throw fail(bound.aborted ? 'unavailable' : 'malformed-session');
  }
  const session = sessionResponseSchema.safeParse(json);
  if (!session.success) throw fail('malformed-session');
  return { session: session.data, landing: resolvePostLoginPath(record.returnTo) };
}
